import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Flex,
  Input,
  Tag,
  TagLabel,
  TagCloseButton,
  Text,
  List,
  ListItem,
  FormControl,
  FormLabel,
  FormHelperText,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

const DEFAULT_MAX_TAGS = 20;
const MAX_TAG_LENGTH = 50;

export interface TagMultiSelectProps {
  /** Currently-selected tags. */
  value: string[];
  /** Selection change callback. */
  onChange: (tags: string[]) => void;
  /** Tag pool to suggest while typing. */
  suggestions?: string[];
  /** When true, free-text values not in suggestions are added as new tags. Default true. */
  allowCreate?: boolean;
  /** Optional cap on selected tags. Defaults to 20 (per-track maximum from FR-017). */
  maxTags?: number;
  /** Optional placeholder when nothing is selected. */
  placeholder?: string;
  /** Component size — `sm` is suitable for filter rows, `md` for forms. Default `sm`. */
  size?: 'sm' | 'md';
  /** Wrap in FormControl + FormLabel. Default true (existing TrackDetailPage behavior). */
  wrapInFormControl?: boolean;
  /** Override the default form label text. Ignored when wrapInFormControl=false. */
  label?: string;
  /** Disable the entire control. */
  isDisabled?: boolean;
  /** Optional test id passed through to the input element. */
  inputTestId?: string;
}

export const TagMultiSelect: React.FC<TagMultiSelectProps> = ({
  value,
  onChange,
  suggestions = [],
  allowCreate = true,
  maxTags = DEFAULT_MAX_TAGS,
  placeholder,
  size = 'sm',
  wrapInFormControl = true,
  label,
  isDisabled = false,
  inputTestId = 'tag-input',
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredSuggestions = suggestions.filter(
    (s) =>
      s.toLowerCase().includes(inputValue.toLowerCase()) &&
      !value.includes(s.toLowerCase())
  );

  const addTag = useCallback(
    (rawValue: string) => {
      const normalized = rawValue.trim().toLowerCase();
      if (!normalized) return;
      if (normalized.length > MAX_TAG_LENGTH) return;
      if (value.length >= maxTags) return;
      if (value.includes(normalized)) return;
      // Filter mode (allowCreate=false) — only accept values present in suggestions.
      if (!allowCreate && !suggestions.some((s) => s.toLowerCase() === normalized)) {
        return;
      }

      onChange([...value, normalized]);
      setInputValue('');
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    },
    [value, maxTags, allowCreate, suggestions, onChange]
  );

  const removeTag = useCallback(
    (tagToRemove: string) => {
      onChange(value.filter((t) => t !== tagToRemove));
    },
    [value, onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredSuggestions.length) {
          addTag(filteredSuggestions[highlightedIndex]);
        } else {
          addTag(inputValue);
        }
      } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
        removeTag(value[value.length - 1]);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }
    },
    [inputValue, value, filteredSuggestions, highlightedIndex, addTag, removeTag]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      if (v.includes(',')) {
        const parts = v.split(',');
        parts.forEach((part) => addTag(part));
        return;
      }
      setInputValue(v);
      setShowSuggestions(v.length > 0);
      setHighlightedIndex(-1);
    },
    [addTag]
  );

  const handleBlur = useCallback(() => {
    if (inputValue.trim()) {
      addTag(inputValue);
    }
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  }, [inputValue, addTag]);

  const isMaxReached = value.length >= maxTags;
  const isInputTooLong = inputValue.length > MAX_TAG_LENGTH;
  const effectivePlaceholder =
    placeholder ?? (value.length === 0 ? t('admin.library.tagsPlaceholder') : '');

  const control = (
    <Box
      position="relative"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      p={size === 'sm' ? 1.5 : 2}
      _focusWithin={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
      cursor={isDisabled ? 'not-allowed' : 'text'}
      opacity={isDisabled ? 0.6 : 1}
      onClick={() => inputRef.current?.focus()}
    >
      <Flex wrap="wrap" gap={1} align="center">
        {value.map((tag) => (
          <Tag
            key={tag}
            size={size}
            colorScheme="blue"
            borderRadius="full"
            data-testid={`tag-${tag}`}
          >
            <TagLabel>{tag}</TagLabel>
            {!isDisabled && (
              <TagCloseButton
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(tag);
                }}
                aria-label={`${t('admin.library.tagsRemove')}: ${tag}`}
                data-testid={`tag-remove-${tag}`}
              />
            )}
          </Tag>
        ))}
        {!isMaxReached && !isDisabled && (
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => inputValue.length > 0 && setShowSuggestions(true)}
            onBlur={handleBlur}
            placeholder={effectivePlaceholder}
            size={size}
            variant="unstyled"
            minW="120px"
            flex={1}
            data-testid={inputTestId}
          />
        )}
      </Flex>

      {showSuggestions && filteredSuggestions.length > 0 && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={10}
          mt={1}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="md"
          maxH="200px"
          overflowY="auto"
          data-testid="tag-suggestions"
        >
          <Text fontSize="xs" color="gray.500" px={3} pt={2} pb={1}>
            {t('admin.library.tagsSuggestions')}
          </Text>
          <List spacing={0}>
            {filteredSuggestions.map((suggestion, index) => (
              <ListItem
                key={suggestion}
                px={3}
                py={1.5}
                cursor="pointer"
                bg={index === highlightedIndex ? 'blue.50' : 'transparent'}
                _hover={{ bg: 'blue.50' }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addTag(suggestion)}
                data-testid={`tag-suggestion-${suggestion}`}
              >
                <Text fontSize="sm">{suggestion}</Text>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );

  if (!wrapInFormControl) {
    return control;
  }

  return (
    <FormControl>
      <FormLabel>{label ?? t('admin.library.tags')}</FormLabel>
      {control}
      {isMaxReached && (
        <FormHelperText color="orange.500" data-testid="tag-max-reached">
          {t('admin.library.tagsMaxReached', { max: maxTags })}
        </FormHelperText>
      )}
      {isInputTooLong && (
        <FormHelperText color="red.500" data-testid="tag-max-length">
          {t('admin.library.tagsMaxLength')}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default TagMultiSelect;
